import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BrandMark from "../components/ui/BrandMark";
import Reveal from "../components/ui/Reveal";
import useLang from "../hooks/useLang";

// Fixed star field so it stays the same between renders.
const STARS = Array.from({ length: 46 }, (_, i) => ({
  top: (i * 37) % 100,
  left: (i * 61 + 13) % 100,
  size: i % 7 === 0 ? 3 : i % 3 === 0 ? 2 : 1,
  delay: (i % 9) * 0.4,
}));

const NotFound = () => {
  const { t, dir } = useLang();
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <div dir={dir} className="font-inter">
      <section className="relative bg-ink text-white overflow-hidden pt-[72px]">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {STARS.map((s, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white animate-pulse"
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                opacity: s.size === 1 ? 0.35 : 0.6,
                animationDelay: `${s.delay}s`,
              }}
            />
          ))}
          <div className="absolute -bottom-40 end-[-10%] h-[560px] w-[560px] rounded-full bg-rose/15 blur-[120px]" />
        </div>

        <div className="relative container-site min-h-[calc(100vh-72px)] py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <span className="eyebrow text-rose-light">{t("notFound.eyebrow")}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 text-[clamp(2.6rem,5vw,4.6rem)] leading-[1.04] tracking-[-0.035em]">
                <span className="font-light block">{t("notFound.title")}</span>
                <span className="font-extrabold block">{t("notFound.titleHeavy")}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="lead text-white/60 max-w-md mt-6">{t("notFound.desc")}</p>
            </Reveal>
            <Reveal delay={0.3} className="flex flex-wrap items-center gap-6 mt-10">
              <Link to="/" className="btn-rose group">
                {t("notFound.home")}
                <ArrowRight size={15} className="rtl:rotate-180" />
              </Link>
              <Link to="/services" className="link-arrow group text-white hover:text-rose-light">
                {t("notFound.solutions")}
                <ArrowRight size={15} className="rtl:rotate-180" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative flex justify-center">
            <span
              className="pointer-events-none select-none absolute inset-0 flex items-center justify-center font-extrabold leading-none tracking-[-0.06em] text-[clamp(10rem,26vw,24rem)] text-transparent [-webkit-text-stroke:1.5px_rgba(244,63,94,0.55)]"
              aria-hidden="true"
              dir="ltr"
            >
              404
            </span>
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -14, 0], rotate: [-1.5, 1.5, -1.5] }}
              transition={{
                opacity: { duration: 0.8 },
                scale: { duration: 0.8 },
                y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 8, repeat: Infinity, ease: "easeInOut" },
              }}
            >
              {imageFailed ? (
                <BrandMark variant="onDark" animated className="w-56 sm:w-72 h-auto" />
              ) : (
                <img
                  src="/images/space-cat.jpg"
                  alt={t("notFound.imageAlt")}
                  onError={() => setImageFailed(true)}
                  className="w-[260px] sm:w-[360px] lg:w-[420px] aspect-square object-cover rounded-[40px] ring-1 ring-white/10 shadow-[0_40px_120px_-30px_rgba(225,29,72,0.65)]"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
